import { When } from 'react-if'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { slugs } from '@/constants/slugs'
import { ProjectMetaProvider } from '@/contexts/ProjectMetaContext'
import { TheatricalRegionProvider } from '@/contexts/TheatricalRegionContext/TheatricalRegionContext'
import { Seo } from '@/layout/Seo'
import { MovieJsonLd } from '@/layout/Seo/JsonLd'
import { NextPageWithLayout } from '@/layouts/types'
import { MarketingVideoPopup } from '@/organisms/MarketingVideoPopup'
import { TheatricalFooter, TheatricalFooterProps } from '@/organisms/TheatricalFooter'
import { getTheatricalReleaseBySlug } from '@/services/CmsService/TheatricReleases'
import {
  CatalogMovie,
  fetchTitle,
  formatTitleFriendlyReleaseDate,
  formatTitleReleaseYear,
  mapJsonLdProps,
  mapSeoProps,
} from '@/services/ContentCatalog'
import { getLightFranchiseBySlug, LightFranchise } from '@/services/FranchiseService'
import { getProjectLivestreams, PlayableLivestream } from '@/services/LivestreamService'
import { inTheaters, isStreaming } from '@/services/PhaseManagerService'
import { isSellingTickets } from '@/services/ProjectPhaseService'
import { getContentfulProjectTheme } from '@/services/ProjectThemeService'
import { getProjectAbout, ProjectMeta } from '@/services/ProjectsService'
import { getLightTheatricalRelease } from '@/services/Theatrical/queries'
import {
  formatFilmName,
  getCurrentYear,
  getTheatricalPreReleasePageData,
  GetTheatricalPreReleasePageDataResult,
  isProjectThatsBothSeriesAndMovie,
  useMoviesTitle,
} from '@/services/TheatricalPreReleaseService'
import { getWhereToWatch, WhereToWatch } from '@/services/WhereToWatchService'
import { ProjectTheme, TheatricalRegion } from '@/types/codegen-contentful'
import { TicketsStatusReference } from '@/types/codegen-federation'
import { getItemOrFirstEntry } from '@/utils/array-utils'
import { isDevelopmentEnvironment } from '@/utils/environment-utils'
import { getStaticPropsErrorHandler } from '@/utils/nextUtils/nextErrorHandlers'
import { noPreRenderedStaticPaths } from '@/utils/nextUtils/staticPages'
import { omitUndefineds } from '@/utils/object'
import { redact } from '@/utils/sensitive-data'
import { shouldHideProjectFromSearchEngines } from '@/utils/seo/searchEngineHelper'
import { loadTranslations } from '@/utils/translate/translate-server'
import { WatchTheatricalView } from '@/views/WatchTheatricalView'
import { WatchTheatricalContextProvider } from '@/views/WatchTheatricalView/WatchTheatricalContext'

interface MoviesPageProps extends ProjectMeta {
  allTheatricalReleaseRegions: TheatricalRegion[]
  catalogTitle: CatalogMovie | null
  inTheaters: boolean
  isStreaming: boolean
  livestreams: PlayableLivestream[]
  locale: string
  pageData: GetTheatricalPreReleasePageDataResult
  whereToWatch: WhereToWatch
  contentfulProjectTheme: ProjectTheme
  supportedTheatricalRegions: TheatricalRegion[]
  canShowTickets?: boolean
  ticketStatus?: TicketsStatusReference | null
  franchise: LightFranchise | null
  projectMeta: ProjectMeta
}

export const MoviesPage: NextPageWithLayout<MoviesPageProps> = (props) => {
  const { filmDetails } = props.pageData
  const { inTheaters, isStreaming, locale, franchise, projectMeta } = props
  const shouldHide = shouldHideProjectFromSearchEngines(props)

  const path = `${paths.movies.index}/${props.slug}`
  const catalogTitle = props.catalogTitle
  const title = useMoviesTitle({
    filmDetails,
    filmName: catalogTitle ? formatFilmName(catalogTitle) : projectMeta.name,
    inTheaters,
    isStreaming:
      props.slug === slugs.cabrini ||
      props.slug === slugs.sight ||
      props.slug === slugs.theBlind ||
      props.slug === slugs.theWay ||
      props.slug === slugs.redemptionOfHenryMyers ||
      props.slug === slugs.sweetwater ||
      props.slug === slugs.surprisedByOxford ||
      props.slug === slugs.somethingToStandForWithMikeRowe ||
      isStreaming,
    releaseYear: formatTitleReleaseYear(catalogTitle) || getCurrentYear(),
    releaseDate: formatTitleFriendlyReleaseDate(props.locale, catalogTitle),
  })

  return (
    <>
      <Seo
        {...mapSeoProps({ path, title, catalogTitle, locale, projectMeta })}
        noindex={shouldHide}
        nofollow={shouldHide}
        path={path}
      />
      {/* TODO: soon, all pages will have catalog titles, and this can be removed */}
      {catalogTitle && <MovieJsonLd {...mapJsonLdProps({ path, catalogTitle, locale })} />}
      <ProjectMetaProvider project={props}>
        <TheatricalRegionProvider
          supportedTheatricalRegions={props.supportedTheatricalRegions}
          regionData={props.allTheatricalReleaseRegions}
        >
          <WatchTheatricalContextProvider pageData={props.pageData} franchise={franchise}>
            <MarketingVideoPopup />
            <WatchTheatricalView
              catalogTitle={catalogTitle}
              livestreams={props.livestreams}
              whereToWatch={props.whereToWatch}
              contentfulProjectTheme={props.contentfulProjectTheme}
              canShowTickets={props.canShowTickets}
              ticketStatus={props.ticketStatus}
            />
            <When condition={props.slug === slugs.soundOfFreedom || props.slug === slugs.theShift}>
              <TheatricalFooter {...(filmDetails?.filmAssociationLogos as TheatricalFooterProps)} slug={props.slug} />
            </When>
          </WatchTheatricalContextProvider>
        </TheatricalRegionProvider>
      </ProjectMetaProvider>
    </>
  )
}

export const getStaticPaths = noPreRenderedStaticPaths

export const getStaticProps = getStaticPropsErrorHandler<MoviesPageProps>(
  { filePath: 'pages/[locale]/movies/[projectSlug].tsx' },
  async ({ federationClient, locale, params, preview, next404 }) => {
    const projectSlug = getItemOrFirstEntry(params.projectSlug)
    if (!projectSlug) return next404()

    const project = projectSlug.toLowerCase()

    const projectMeta = await getProjectAbout({ slug: project }, federationClient, { locale, preview })
    if (!projectMeta) {
      return {
        redirect: {
          destination: paths.movies.index,
          permanent: false,
        },
      }
    }

    const shouldIncludePreview = isDevelopmentEnvironment()
    const [
      livestreams,
      pageData,
      catalogTitle,
      theatricalRelease,
      theatricalReleaseMono,
      whereToWatch,
      contentfulProjectTheme,
      translations,
    ] = await Promise.all([
      getProjectLivestreams(projectMeta, project, federationClient),
      getTheatricalPreReleasePageData({ slug: project }, federationClient, { locale, preview }),
      fetchTitle<CatalogMovie>(locale, projectMeta.contentCatalogId),
      getTheatricalReleaseBySlug({
        theatricalSlug: project,
        opts: { preview: shouldIncludePreview, locale },
      }),
      getLightTheatricalRelease({
        theatricalSlug: project,
        client: federationClient,
      }),
      getWhereToWatch(project, preview),
      getContentfulProjectTheme(project, preview),
      loadTranslations(locale, [
        'common',
        'home',
        'watch',
        'app-promo',
        'ads',
        'account',
        'theatrical-presales',
        'pif',
        'guild',
      ]),
    ])

    const ticketStatus = theatricalReleaseMono?.region?.ticketsStatus ?? null
    const canShowTickets = isSellingTickets(ticketStatus)

    const allTheatricalReleaseRegions = theatricalRelease?.regionCollection || []
    const supportedTheatricalRegions = theatricalRelease?.supportedTheatricalRegions || []

    let franchise
    if (projectMeta.franchiseDetails?.slug) {
      franchise = await getLightFranchiseBySlug(projectMeta.franchiseDetails.slug)
    }

    const redirect = getRedirect(project)
    if (redirect) return redirect

    if (projectMeta?.projectType !== 'movie' && !isProjectThatsBothSeriesAndMovie(projectMeta)) {
      return {
        redirect: {
          destination: paths.movies.index,
          permanent: false,
        },
      }
    }

    return {
      props: {
        allTheatricalReleaseRegions,
        catalogTitle: omitUndefineds(catalogTitle) ?? null,
        inTheaters: inTheaters(projectMeta.primaryFlowPhases ?? []),
        isStreaming: isStreaming(projectMeta.primaryFlowPhases ?? []),
        livestreams,
        locale,
        pageData,
        supportedTheatricalRegions,
        whereToWatch,
        contentfulProjectTheme,
        canShowTickets,
        ticketStatus,
        franchise: omitUndefineds(franchise) ?? null,
        projectMeta,
        ...redact(projectMeta, ['primaryFlowPhases']),
        ...translations,
      },
      revalidate: catalogTitle ? Durations.FIFTEEN_MINUTES_IN_SECONDS : Durations.ONE_MINUTE_IN_SECONDS,
    }
  },
)

export default MoviesPage

const getRedirect = (project: string) => {
  if (project === 'the-last-rodeo') {
    return {
      redirect: {
        destination: paths.movies.theLastRodeo,
        permanent: false,
      },
    }
  }

  if (project === 'angels-sing') {
    return {
      redirect: {
        destination: paths.watch.index,
        permanent: false,
      },
    }
  }

  if (project === slugs.dryBar) {
    return {
      redirect: {
        destination: paths.shows.dryBar,
        permanent: false,
      },
    }
  }

  if (project === slugs.truthAndConviction) {
    return {
      redirect: {
        destination: paths.movies.truthAndTreason,
        permanent: false,
      },
    }
  }

  if (project === slugs.truthAndTreasonSeries) {
    return {
      redirect: {
        destination: paths.shows.truthAndTreasonSeries,
        permanent: false,
      },
    }
  }

  return null
}
